// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import {
  Order,
  core_apimessages_Country as Country,
  MultiOrderCustomsInfoQueryQuery,
  MultiOrderCustomsInfoQueryQueryVariables,
  core_apimessages_Address,
} from '@reverbdotcom/commons/src/gql/graphql';
import { IMoney } from '../../../schemas/core-schema';
import {
  ICustomsInfo,
  ICustomsSignerInfo,
  findCustomsInfoForOrder,
} from './customs_info_utils';
import {
  overCustomsLimit,
  formattedDeclaredValueTotal,
  formattedMaximumAllowedValue,
} from './customs_limit_utils';
import ControlledInput from '@reverbdotcom/commons/src/components/controlled_input';
import I18n from 'i18n-js';
import bind from '@reverbdotcom/commons/src/bind';
import ControlledCheckbox from '@reverbdotcom/commons/src/components/controlled_checkbox';
import OrderCustomsInfo from './order_customs_info';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

interface IProps {
  selectedOrders: Order[];
  customsInfo: ICustomsInfo[];
  customsSignerInfo: ICustomsSignerInfo;
  updateCustomsInfo: Function;
  updateCustomsSignerInfo: Function;
  fromAddress: core_apimessages_Address;
  countries: Country[];
  exchangeRates: MultiOrderCustomsInfoQueryQuery['exchangeRates']['rates'];
}

export class MultiOrderCustomsInfo extends React.Component<IProps, null> {
  @bind
  shipmentCurrency() {
    const firstOrder = this.props.selectedOrders[0];
    return firstOrder.settlementAmountProductSubtotal.currency;
  }

  @bind
  updateCustomsSignerInfo(attrs: Partial<ICustomsSignerInfo>) {
    const signerInfo = { ...this.props.customsSignerInfo, ...attrs };
    return this.props.updateCustomsSignerInfo(signerInfo);
  }

  @bind
  updateDeclaredValue(order: Order, declaredValue: IMoney) {
    const orderCustomsInfo = this.customsInfoForOrder(order);
    orderCustomsInfo.declaredValue = declaredValue;
    return this.props.updateCustomsInfo(this.props.customsInfo);
  }

  @bind
  updateDescription(order: Order, description) {
    const orderCustomsInfo = this.customsInfoForOrder(order);
    orderCustomsInfo.description = description;
    return this.props.updateCustomsInfo(this.props.customsInfo);
  }

  @bind
  updateCountryOfOrigin(order: Order, countryCode) {
    const orderCustomsInfo = this.customsInfoForOrder(order);
    orderCustomsInfo.countryOfOrigin = countryCode;
    return this.props.updateCustomsInfo(this.props.customsInfo);
  }

  @bind
  customsInfoForOrder(order) {
    return findCustomsInfoForOrder(order.id, this.props.customsInfo);
  }

  @bind
  overCustomsLimit(): boolean {
    return overCustomsLimit(
      this.props.fromAddress.countryCode,
      this.props.selectedOrders,
      this.props.customsInfo,
      this.props.exchangeRates,
    );
  }

  @bind
  renderOrdersCustoms() {
    return this.props.selectedOrders.map((order) => {
      const orderCustomsInfo = this.customsInfoForOrder(order);

      return (
        <OrderCustomsInfo
          key={order.id}
          order={order}
          customsInfo={orderCustomsInfo}
          updateDeclaredValue={this.updateDeclaredValue}
          updateDescription={this.updateDescription}
          updateCountryOfOrigin={this.updateCountryOfOrigin}
          countries={this.props.countries}
          isOverCustomsLimit={this.overCustomsLimit()}
          customsLimitWarningText={this.customsLimitWarningText()}
        />
      );
    });
  }

  @bind
  renderSignatureField() {
    return (
      <div>
        <hr className="cms-hr" />
        <div className="g-container">
          <div className="g-col-6 g-offset-6 g-col-mobile-12 g-offset-mobile-0">

            <div className="form-group mb-0">
              <div className="form-group__header">
                <label htmlFor="customs_signer">
                  {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.firstAndLastName')}
                </label>
              </div>
              <div className="form-group__fields">
                <ControlledInput
                  fieldName="customsSigner"
                  inputName="customs_signer"
                  inputValue={this.props.customsSignerInfo.customsSigner}
                  required
                  inputType="text"
                  updateField={this.updateCustomsSignerInfo}
                  className="string"
                />
                <p className="form-group__help-text">
                  {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.certifyTooltipHtml')}
                </p>
                <div className="label-with-checkbox mtb-4">
                  <ControlledCheckbox
                    checked={this.props.customsSignerInfo.certifiesAccuracy}
                    fieldName="certifiesAccuracy"
                    inputName="customs_certifies_accuracy"
                    updateField={this.updateCustomsSignerInfo}
                  />
                  <label htmlFor="customs_certifies_accuracy">
                    {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.certifyAccuracy')}
                  </label>
                </div>
                {this.showFormErrors()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  @bind
  showFormErrors() {
    return null;
  }

  @bind
  customsExplainer() {
    return (
      I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.customsExplainerHtml', { linkOpen: '<a href="https://about.usps.com/publications/pub141/standard-prohibited-and-restricted-items.htm" target="_blank">', linkClose: '</a>' })
    );
  }

  @bind
  customsLimitWarningText() {
    return (
      I18n.t(
        'discovery.dashboard.selling.shippingLabels.orderCustoms.aboveMaximumAllowedDeclaredValue',
        {
          maximumAmount: formattedMaximumAllowedValue(
            this.props.fromAddress.countryCode,
            this.shipmentCurrency(),
            this.props.exchangeRates,
          ),
          declaredAmount: formattedDeclaredValueTotal(
            this.props.selectedOrders,
            this.props.customsInfo,
          ),
        },
      )
    );
  }

  render() {
    return (
      <div className="shipping-label-form__module">
        <h3 className="size-120 weight-bold mb-2">
          {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.customsTitle')}
        </h3>
        <p className="mb-2">
          <SanitizedRender html={this.customsExplainer()} />
        </p>
        {this.renderOrdersCustoms()}
        {this.renderSignatureField()}
      </div>
    );
  }
}

export const MULTI_ORDER_CUSTOMS_INFO_QUERY = gql`
  query MultiOrderCustomsInfoQuery {
    exchangeRates(input: { inverseReverseRates: true }) {
      rates {
        from
        to
        rate
      }
    }
  }
`;

const connect = withGraphql<Partial<IProps>, MultiOrderCustomsInfoQueryQuery, MultiOrderCustomsInfoQueryQueryVariables>(
  MULTI_ORDER_CUSTOMS_INFO_QUERY,
  {
    alias: 'WrappedMultiOrderCustomsInfo',
    options: {
      ssr: false, // not important for SEO or initial page rendering
    },
  },
);

export default connect((props) => {
  if (props.data.loading) {
    return null;
  }
  return (
    <MultiOrderCustomsInfo
      selectedOrders={props.selectedOrders}
      customsInfo={props.customsInfo}
      customsSignerInfo={props.customsSignerInfo}
      updateCustomsInfo={props.updateCustomsInfo}
      updateCustomsSignerInfo={props.updateCustomsSignerInfo}
      fromAddress={props.fromAddress}
      countries={props.countries}
      exchangeRates={props.data.exchangeRates.rates}
    />
  );
});
